import { useEffect, useState } from "react";
import { HoldCleaningWorkflowModal, WorkflowField, workflowInputClass } from "./HoldCleaningWorkflowModal";

const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
const holdLimit = (numberOfHolds?: number | null) =>
  numberOfHolds && Number.isInteger(numberOfHolds) && numberOfHolds > 0 ? Math.min(numberOfHolds, 50) : 50;

export const HoldCleaningPhotoUploadModal = ({
  open,
  numberOfHolds,
  onClose,
  onSave,
}: {
  open: boolean;
  numberOfHolds: number | null | undefined;
  onClose: () => void;
  onSave: (files: File[], holdNumber: number | null, caption: string) => Promise<void>;
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [hold, setHold] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setFiles([]); setHold(""); setCaption(""); setError(null); } }, [open]);
  const submit = async () => {
    const holdNumber = hold ? Number(hold) : null;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (!files.length || files.length > 10) { setError("Select between one and ten photos."); return; }
    if (files.some((file) => !allowedTypes.has(file.type))) { setError("Only JPEG, PNG and WebP images are supported."); return; }
    if (totalBytes > 50 * 1024 * 1024) { setError("The combined upload cannot exceed 50 MB."); return; }
    if (numberOfHolds && holdNumber === null) { setError("Select a hold for these photos."); return; }
    if (holdNumber !== null && (!Number.isInteger(holdNumber) || holdNumber < 1 || holdNumber > holdLimit(numberOfHolds))) {
      setError(`Hold number must be between 1 and ${holdLimit(numberOfHolds)}.`); return;
    }
    setBusy(true); setError(null);
    try { await onSave(files, holdNumber, caption); onClose(); } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The photos could not be uploaded.");
    } finally { setBusy(false); }
  };
  return (
    <HoldCleaningWorkflowModal open={open} title="Upload photos" description="Choose up to 10 photos for one hold. They are scanned and stored privately." busy={busy} error={error} submitLabel="Upload photos" onClose={onClose} onSubmit={submit}>
      <div className="grid gap-5 sm:grid-cols-2">
        <WorkflowField label="Photos">
          <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(event) => setFiles(Array.from(event.target.files ?? []))} className={workflowInputClass} />
        </WorkflowField>
        <WorkflowField label={`Hold${numberOfHolds ? " (required)" : ""}`}>
          {numberOfHolds && numberOfHolds > 0 ? (
            <select value={hold} onChange={(event) => setHold(event.target.value)} className={workflowInputClass}>
              <option value="">Choose a hold</option>
              {Array.from({ length: holdLimit(numberOfHolds) }, (_, index) => <option key={index + 1} value={index + 1}>Hold {index + 1}</option>)}
            </select>
          ) : <input type="number" min={1} max={50} value={hold} onChange={(event) => setHold(event.target.value)} placeholder="Hold number, if known" className={workflowInputClass} />}
        </WorkflowField>
        <div className="sm:col-span-2"><WorkflowField label="Caption (optional)">
          <input value={caption} maxLength={1000} onChange={(event) => setCaption(event.target.value)} className={workflowInputClass} />
        </WorkflowField></div>
      </div>
      <p className="mt-4 text-xs text-[#4A5655] dark:text-gray-300">{files.length ? `${files.length} file(s), ${(files.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(1)} MB` : "No files selected"} · All selected photos will be assigned to the same hold.</p>
    </HoldCleaningWorkflowModal>
  );
};
